import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeviceService } from '@omni/services/device/device.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import {
  ExpenseUploadStep,
  MeetingExpenseDoc,
  OnlineNoteChange,
  completeStep,
  isDraftStatusCode,
  isOfflineMeetingId,
  markCreateSent,
  markCreated,
  meetingKey,
  planUpload,
} from '@omni/classes/meeting-expense/meeting-expense.pending';
import { Subject } from 'rxjs';
import { Guid } from 'typescript-guid';
import { TimeoutError } from '@omni/utility/util';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { MeetingMandatoryActivitiesConfigService } from '@omni/services/meeting/meeting-mandatory-activities-config.service';
import { MeetingExpenseDataService } from '@omni/data-services/meeting-expense/meeting-expense.data.service';
import { StagedSdrNote } from '@omni/data-services/sample/sample-delivery-request.data.service';
import { NotesAttachmentsConfig, parseNotesAttachmentsConfig } from '@omni/utility/notes-attachments-config.util';
import {
  ExpenseMasterData,
  ExpenseStatus,
  MeetingExpense,
  expenseStatus,
  isExpenseCancelled,
} from '@omni/classes/meeting-expense/meeting-expense.class';
import {
  EXPENSE_STATUS_CODE,
  MEETING_EXPENSE_INLINE_ACTIVITY_KEY,
  MEETING_EXPENSE_NOTES_ACTIVITY_TYPES,
} from '@omni/classes/meeting-expense/meeting-expense.constants';
import {
  ExpenseDateBounds,
  FormatChangeExpenseAction,
  canSubmitExpense,
  defaultExpenseDate,
  expenseDateBounds,
  formatChangeExpenseAction,
  gracePeriodDaysFromEntry,
} from './meeting-expense.rules';

export interface ExpenseSweepResult {
  submitted: number;
  incomplete: number;
  failed: number;
}

type UploadOutcome = 'uploaded' | 'deferred' | 'conflict' | 'reverted' | 'rejected' | 'failed';

const SAVED_OFFLINE = 'SAVED_OFFLINE_WILL_SYNC';

type WriteFailure = 'rejected' | 'transient' | 'other';

// 'other' (no HTTP status, e.g. a coding error) is never queued: it must surface, not hide in the offline queue.
function classifyWriteFailure(err: unknown): WriteFailure {
  if (err instanceof TimeoutError) return 'transient';
  const raw = err as { status?: unknown; error?: { status?: unknown } } | undefined;
  const value = raw?.status ?? raw?.error?.status;
  const status = value == null || value === '' ? NaN : Number(value);
  if (!Number.isFinite(status)) return 'other';
  const retryable = status < 400 || status >= 500 || status === 401 || status === 408 || status === 429;
  return retryable ? 'transient' : 'rejected';
}

const isRejectedByServer = (err: unknown): boolean => classifyWriteFailure(err) === 'rejected';

@Injectable({ providedIn: 'root' })
export class MeetingExpenseService {
  readonly expensesRefresh$ = new Subject<string>();
  private masterData: ExpenseMasterData | null = null;
  private masterDataFromServer = false;
  private masterDataFetch: Promise<ExpenseMasterData> | null = null;
  private readonly meetingLoads = new Map<string, Promise<MeetingExpense[]>>();
  private readonly persistChains = new Map<string, Promise<void>>();
  private readonly noteIds = new Map<string, Promise<string | undefined>>();
  private readonly activeExpenseIds = new Map<string, Map<string, number>>();
  private uploading: Promise<void> | null = null;

  constructor(
    private readonly dataService: MeetingExpenseDataService,
    private readonly authService: AuthenticationService,
    private readonly formatConfig: MeetingMandatoryActivitiesConfigService,
    private readonly device: DeviceService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {}

  isEnabled(): boolean {
    return this.authService.hasFeatureAction(FeatureActionsMap.MEETING_EXPENSES);
  }

  isSectionVisibleForFormat(formatId: string | null | undefined): boolean {
    return this.isEnabled() && !this.formatConfig.shouldHideInlineActivityForFormat(formatId, MEETING_EXPENSE_INLINE_ACTIVITY_KEY);
  }

  // Without the feature action the rep cannot add an expense, so a mandatory rule must not block completion.
  satisfiesMandatoryRule(meetingId: string | null | undefined): boolean {
    return !this.isEnabled() || this.hasActiveExpenses(meetingId);
  }

  clearSessionState(): void {
    this.masterData = null;
    this.masterDataFromServer = false;
    this.activeExpenseIds.clear();
  }

  gracePeriodDays(formatId: string | null | undefined): number {
    return gracePeriodDaysFromEntry(this.formatConfig.getConfigForFormatId(formatId));
  }

  dateBounds(today: Date = new Date()): ExpenseDateBounds {
    return expenseDateBounds(today, this.maxBackDays);
  }

  initialExpenseDate(meetingStart: Date | null, today: Date = new Date()): string {
    return defaultExpenseDate(meetingStart, today, this.maxBackDays);
  }

  private get maxBackDays(): number {
    return this.authService.user?.maxMeetingStartDays ?? 3650;
  }

  notesConfig(): NotesAttachmentsConfig {
    return parseNotesAttachmentsConfig(
      this.authService.getIOConfigurationValue(IoConfigurationFeatureCodes.BU_ACTIVITY_GA_NOTES_ATTACHMENTS),
      MEETING_EXPENSE_NOTES_ACTIVITY_TYPES,
    );
  }

  /** Fetches from the server once per session; `refresh` (used by sync) fetches again. */
  async loadMasterData(isOnline: boolean, refresh = false): Promise<ExpenseMasterData | null> {
    if (this.masterData && this.masterDataFromServer && !refresh) return this.masterData;
    if (isOnline) {
      try {
        this.masterDataFetch ??= this.dataService.fetchMasterData().finally(() => { this.masterDataFetch = null; });
        this.masterData = await this.masterDataFetch;
        this.masterDataFromServer = true;
        return this.masterData;
      } catch (err) {
        console.error('MeetingExpenseService.loadMasterData: fetch failed, using cache', err);
      }
    }
    this.masterData = this.masterData ?? (await this.dataService.getCachedMasterData());
    return this.masterData;
  }

  /** Concurrent loads of the same meeting share one fetch. */
  loadForMeeting(meetingId: string, isOnline: boolean): Promise<MeetingExpense[]> {
    const online = isOnline && !isOfflineMeetingId(meetingId);
    const loadKey = `${meetingKey(meetingId)}|${online}`;
    const inFlight = this.meetingLoads.get(loadKey);
    if (inFlight) return inFlight;
    const load = this.fetchForMeetingAndIndex(meetingId, online).finally(() => {
      if (this.meetingLoads.get(loadKey) === load) this.meetingLoads.delete(loadKey);
    });
    this.meetingLoads.set(loadKey, load);
    return load;
  }

  private async fetchForMeetingAndIndex(meetingId: string, online: boolean): Promise<MeetingExpense[]> {
    const key = meetingKey(meetingId);
    const createdBefore = new Set(this.activeExpenseIds.get(key)?.keys() ?? []);
    const expenses = await (online ? this.dataService.fetchForMeeting(meetingId) : this.dataService.getCachedForMeeting(meetingId));
    const live = new Map<string, number>(
      expenses.filter((expense) => !isExpenseCancelled(expense)).map((expense): [string, number] => [expense.id, expense.statusCode]),
    );
    // A draft added while this fetch was in flight is missing from its result.
    for (const [id, statusCode] of this.activeExpenseIds.get(key) ?? []) {
      if (!createdBefore.has(id)) live.set(id, statusCode);
    }
    this.activeExpenseIds.set(key, live);
    return expenses;
  }

  async rebuildIndexFromCache(): Promise<void> {
    const rebuilt = new Map<string, Map<string, number>>();
    for (const expense of await this.dataService.getAllCached()) {
      if (isExpenseCancelled(expense)) continue;
      const key = meetingKey(expense.meetingId);
      const statuses = rebuilt.get(key) ?? new Map<string, number>();
      statuses.set(expense.id, expense.statusCode);
      rebuilt.set(key, statuses);
    }
    this.activeExpenseIds.clear();
    for (const [key, statuses] of rebuilt) this.activeExpenseIds.set(key, statuses);
  }

  // Synchronous on purpose: the completion check cannot await. Filled by sync and refreshed when a section loads.
  hasActiveExpenses(meetingId: string | null | undefined): boolean {
    return this.isEnabled() && !!meetingId && (this.activeExpenseIds.get(meetingKey(meetingId))?.size ?? 0) > 0;
  }

  hasNonDraftExpenses(meetingId: string | null | undefined): boolean {
    return this.someStatus(meetingId, (statusCode) => !isDraftStatusCode(statusCode));
  }

  hasDraftExpenses(meetingId: string | null | undefined): boolean {
    return this.someStatus(meetingId, isDraftStatusCode);
  }

  // A push-only upload can refill the index after sync cleared it for a user without the feature action.
  private someStatus(meetingId: string | null | undefined, test: (statusCode: number) => boolean): boolean {
    if (!this.isEnabled()) return false;
    const statuses = meetingId ? this.activeExpenseIds.get(meetingKey(meetingId)) : undefined;
    return !!statuses && [...statuses.values()].some(test);
  }

  private trackActive(meetingId: string, expenseId: string, statusCode: number): void {
    const key = meetingKey(meetingId);
    const statuses = this.activeExpenseIds.get(key) ?? new Map<string, number>();
    statuses.set(expenseId, statusCode);
    this.activeExpenseIds.set(key, statuses);
  }

  private untrack(meetingId: string, expenseId: string): void {
    this.activeExpenseIds.get(meetingKey(meetingId))?.delete(expenseId);
  }

  async formatChangeAction(meetingId: string | null | undefined, newFormatId: string, isOnline: boolean): Promise<FormatChangeExpenseAction> {
    if (!meetingId || !this.isEnabled()) return 'none';
    const showsExpenses = this.isSectionVisibleForFormat(newFormatId);
    if (showsExpenses) return 'none';
    await this.loadForMeeting(meetingId, isOnline).catch(() => this.loadForMeeting(meetingId, false));
    return formatChangeExpenseAction(showsExpenses, this.hasNonDraftExpenses(meetingId), this.hasDraftExpenses(meetingId));
  }

  /** Resolves with how many Drafts could not be scrapped. */
  async scrapDraftsForMeeting(meetingId: string): Promise<number> {
    const isOnline = !this.device.isOffline;
    const expenses = await this.loadForMeeting(meetingId, isOnline).catch(() => this.loadForMeeting(meetingId, false));
    const drafts = expenses.filter((expense) => expenseStatus(expense) === ExpenseStatus.Draft);
    const results = await Promise.all(drafts.map((expense) => this.scrap(expense, false).then(() => true, (err) => {
      console.error('MeetingExpenseService.scrapDraftsForMeeting failed', expense.id, err);
      return false;
    })));
    return results.filter((scrapped) => !scrapped).length;
  }

  createsOnServer(meetingId: string): boolean {
    return !this.device.isOffline && !isOfflineMeetingId(meetingId);
  }

  async createDraft(meetingId: string, expenseDate: string): Promise<MeetingExpense> {
    const expense: MeetingExpense = {
      id: Guid.create().toString(),
      meetingId,
      ownerName: this.authService.user?.displayName ?? '',
      expenseDate,
      contactId: null,
      contactName: null,
      typeId: null,
      typeName: null,
      subtypeId: null,
      subtypeName: null,
      amount: null,
      statusCode: EXPENSE_STATUS_CODE.DRAFT,
      serverName: null,
      notes: [],
    };
    let created: MeetingExpense;
    if (this.createsOnServer(meetingId)) {
      [created] = await this.onlineElseQueue(
        () => this.dataService.create(expense, this.authService.user?.defaultCurrencyID ?? null).then(() => expense),
        () => this.dataService.stageCreate(expense, true),
        SAVED_OFFLINE,
      );
    } else {
      created = await this.dataService.stageCreate(expense);
      this.notifyQueued(SAVED_OFFLINE);
    }
    this.trackActive(meetingId, expense.id, EXPENSE_STATUS_CODE.DRAFT);
    return created;
  }

  async save(expense: MeetingExpense): Promise<void> {
    await this.enqueue(expense.id, () =>
      this.persist(expense.id, () => this.dataService.update(expense), () => this.dataService.stageFields(expense), SAVED_OFFLINE));
  }

  async submit(expense: MeetingExpense, announce = true): Promise<MeetingExpense> {
    const [submitted, queued] = await this.enqueue(expense.id, () =>
      this.persist(expense.id, () => this.dataService.submit(expense), () => this.dataService.stageSubmit(expense), null));
    if (queued && announce) this.notifyQueued('SUBMITTED_OFFLINE_WILL_SYNC');
    this.trackActive(expense.meetingId, expense.id, submitted.statusCode);
    return submitted;
  }

  async submitCompleteDrafts(meetingId: string, isOnline: boolean): Promise<ExpenseSweepResult> {
    // An autosave or note write still in flight would otherwise be read back stale, then hit a deactivated record.
    await Promise.all([...this.persistChains.values()]);
    const online = isOnline && !isOfflineMeetingId(meetingId);
    const expenses = await (online ? this.dataService.fetchForMeeting(meetingId) : this.dataService.getCachedForMeeting(meetingId));
    const drafts = expenses.filter((expense) => expenseStatus(expense) === ExpenseStatus.Draft);
    if (!drafts.length) return { submitted: 0, incomplete: 0, failed: 0 };
    const master = await this.loadMasterData(online);
    if (!master) return { submitted: 0, incomplete: drafts.length, failed: 0 };
    const notesConfig = this.notesConfig();
    const complete = drafts.filter((expense) => canSubmitExpense(expense, master, notesConfig));
    const incomplete = drafts.length - complete.length;
    if (!complete.length) return { submitted: 0, incomplete, failed: 0 };
    // Independent writes, not a DWA batch: a batch is one atomic changeset, so one rejected PATCH would roll back every submit.
    const outcomes = await Promise.allSettled(complete.map((expense) => this.submit(expense, false)));
    const failed = outcomes.filter((outcome) => outcome.status === 'rejected');
    for (const outcome of failed) console.error('MeetingExpenseService.submitCompleteDrafts rejected', (outcome as PromiseRejectedResult).reason);
    this.announceRefresh(meetingId);
    return { submitted: outcomes.length - failed.length, incomplete, failed: failed.length };
  }

  async scrap(expense: MeetingExpense, announce = true): Promise<MeetingExpense> {
    const [scrapped] = await this.enqueue(expense.id, () => this.persist(
      expense.id,
      () => this.dataService.scrap(expense),
      () => this.dataService.stageScrap(expense),
      announce ? SAVED_OFFLINE : null,
    ));
    this.untrack(expense.meetingId, expense.id);
    return scrapped;
  }

  createNote(expenseId: string, clientId: string, note: StagedSdrNote): Promise<string | undefined> {
    const created = this.enqueue(expenseId, async () => {
      const [annotationId, queued] = await this.persist(
        expenseId,
        () => this.dataService.createNote(expenseId, note),
        () => this.dataService.stageNote(expenseId, note, false).then(() => undefined),
        SAVED_OFFLINE,
      );
      if (!queued && annotationId) await this.cacheOnlineNote(expenseId, { kind: 'upsert', annotationId, note, clearAttachment: false });
      return annotationId;
    });
    this.noteIds.set(clientId, created.then(
      (annotationId) => annotationId,
      () => {
        this.noteIds.delete(clientId);
        return undefined;
      },
    ));
    return created;
  }

  // Outlives the form, so a note added before a row was collapsed can still be edited or removed after reopening.
  resolveAnnotationId(clientId: string): Promise<string | undefined> {
    return this.noteIds.get(clientId) ?? Promise.resolve(undefined);
  }

  updateNote(expenseId: string, note: StagedSdrNote, clearAttachment: boolean): Promise<void> {
    return this.enqueue(expenseId, async () => {
      const annotationId = await this.annotationIdOf(note);
      const stage = () => this.dataService.stageNote(expenseId, { ...note, existingAnnotationId: annotationId }, clearAttachment);
      if (!annotationId) return stage();
      const [, queued] = await this.persist(expenseId, () => this.dataService.updateNote(annotationId, note, clearAttachment), stage, SAVED_OFFLINE);
      if (!queued) await this.cacheOnlineNote(expenseId, { kind: 'upsert', annotationId, note, clearAttachment });
    });
  }

  removeNote(expenseId: string, note: StagedSdrNote): Promise<void> {
    return this.enqueue(expenseId, async () => {
      const annotationId = await this.annotationIdOf(note);
      const stage = () => this.dataService.stageNoteRemove(expenseId, { ...note, existingAnnotationId: annotationId });
      if (!annotationId) return stage();
      const [, queued] = await this.persist(expenseId, () => this.dataService.removeNote(annotationId), stage, SAVED_OFFLINE);
      if (!queued) await this.cacheOnlineNote(expenseId, { kind: 'remove', annotationId });
    });
  }

  // The note is already on the server; a cache failure must not make the form discard it.
  private async cacheOnlineNote(expenseId: string, change: OnlineNoteChange): Promise<void> {
    try {
      await this.dataService.cacheNoteChange(expenseId, change);
    } catch (err) {
      console.error('MeetingExpenseService.cacheOnlineNote failed', expenseId, err);
    }
  }

  // A note created earlier in this expense's queue has settled by the time a later write runs.
  private async annotationIdOf(note: StagedSdrNote): Promise<string | undefined> {
    return note.existingAnnotationId ?? (note.clientId ? this.resolveAnnotationId(note.clientId) : undefined);
  }

  private async writesLocally(expenseId: string): Promise<boolean> {
    return this.device.isOffline || (await this.dataService.isPending(expenseId));
  }

  /** Resolves with the result and whether it was queued on the device instead of written to Dynamics. */
  private async persist<T>(
    expenseId: string,
    online: () => Promise<T>,
    local: () => Promise<T>,
    fallbackToastKey: string | null,
  ): Promise<[T, boolean]> {
    if (await this.writesLocally(expenseId)) return [await local(), true];
    return this.onlineElseQueue(online, local, fallbackToastKey);
  }

  // A network error, timeout or 5xx may still have landed; the upload reconciles the queued copy by id.
  private async onlineElseQueue<T>(online: () => Promise<T>, local: () => Promise<T>, fallbackToastKey: string | null): Promise<[T, boolean]> {
    try {
      return [await online(), false];
    } catch (err) {
      if (classifyWriteFailure(err) !== 'transient') throw err;
      console.warn('MeetingExpenseService: online write failed, queued for the next sync', err);
      const queued = await local();
      if (fallbackToastKey) this.notifyQueued(fallbackToastKey);
      return [queued, true];
    }
  }

  /** Pushes every expense with unsynced changes. Chained after the meeting upload, so offline meeting ids resolve. */
  uploadPending(): Promise<void> {
    if (!this.uploading) this.uploading = this.runUploadPending().finally(() => { this.uploading = null; });
    return this.uploading;
  }

  private async runUploadPending(): Promise<void> {
    if (this.device.isOffline) return;
    const docs = await this.dataService.getPendingDocs();
    if (!docs.length) return;
    const server = await this.dataService.fetchServerExpenses(docs.map((doc) => doc.expense.id));
    const tally: Record<UploadOutcome, number> = { uploaded: 0, deferred: 0, conflict: 0, reverted: 0, rejected: 0, failed: 0 };
    const touchedMeetings = new Set<string>();
    const sent: string[] = [];
    for (const doc of docs) {
      const id = doc.expense.id;
      const outcome = await this.enqueue(id, () => this.uploadOne(id, server.get(meetingKey(id))))
        .catch((err): UploadOutcome => {
          console.error('MeetingExpenseService.uploadPending failed', id, err);
          return 'failed';
        });
      tally[outcome]++;
      if (outcome !== 'deferred') touchedMeetings.add(this.dataService.resolveMeeting(doc.expense.meetingId) ?? doc.expense.meetingId);
      if (outcome === 'uploaded' || outcome === 'reverted' || outcome === 'rejected') sent.push(id);
    }
    await this.dataService.refreshNotes(sent).catch((err) => console.error('MeetingExpenseService.refreshNotes failed', err));
    this.notifyUpload('MEETING_EXPENSE_SYNC_CONFLICT', tally.conflict, ToastStyle.INFO);
    this.notifyUpload('MEETING_EXPENSE_SUBMIT_REVERTED', tally.reverted, ToastStyle.DANGER);
    this.notifyUpload('MEETING_EXPENSE_SYNC_REJECTED', tally.rejected, ToastStyle.DANGER);
    this.notifyUpload('MEETING_EXPENSE_SYNC_FAILED', tally.failed, ToastStyle.DANGER);
    for (const meetingId of touchedMeetings) this.announceRefresh(meetingId);
  }

  // A load already in flight read the server before these writes; the reload must not join it.
  private announceRefresh(meetingId: string): void {
    for (const online of [true, false]) this.meetingLoads.delete(`${meetingKey(meetingId)}|${online}`);
    this.expensesRefresh$.next(meetingId);
  }

  private async uploadOne(expenseId: string, server: MeetingExpense | undefined): Promise<UploadOutcome> {
    let doc = await this.dataService.getDoc(expenseId);
    if (!doc?.pending) return 'uploaded';
    const plan = planUpload(doc.pending, server?.statusCode);
    if (plan.kind === 'alreadyApplied') {
      await this.adoptServerCopy(doc, server);
      return 'uploaded';
    }
    if (plan.kind !== 'steps') {
      await this.adoptServerCopy(doc, server);
      return 'conflict';
    }
    if (!plan.steps.length && doc.pending.scrap) {
      await this.dataService.removeLocal(expenseId);
      this.untrack(doc.expense.meetingId, expenseId);
      return 'uploaded';
    }
    if (server && doc.pending.created) {
      doc = markCreated(doc);
      await this.dataService.putDoc(doc);
    }
    if (plan.steps.some((step) => step.kind === 'create')) {
      const meetingId = this.dataService.resolveMeeting(doc.expense.meetingId);
      if (!meetingId) return 'deferred';
      if (meetingId !== doc.expense.meetingId) {
        this.untrack(doc.expense.meetingId, expenseId);
        doc = { ...doc, expense: { ...doc.expense, meetingId } };
        this.trackActive(meetingId, expenseId, doc.expense.statusCode);
      }
      doc = markCreateSent(doc);
      await this.dataService.putDoc(doc);
    }
    const sendsNewNote = plan.steps.some((step) => step.kind === 'note' && !step.entry.note.existingAnnotationId);
    const serverNoteIds = server && sendsNewNote ? await this.dataService.fetchNoteIds(expenseId) : new Set<string>();
    let skippedNotes = 0;
    for (const step of plan.steps) {
      try {
        await this.runUploadStep(doc, step, serverNoteIds);
      } catch (err) {
        console.error('MeetingExpenseService.uploadOne step failed', expenseId, step.kind, err);
        if (!isRejectedByServer(err)) return 'failed';
        if (step.kind === 'submit') return this.revertSubmit(doc);
        if (step.kind !== 'note' && step.kind !== 'noteDelete') return this.discardRejected(doc);
        skippedNotes++;
      }
      const next = completeStep(doc, step);
      if (!next) {
        await this.dataService.removeLocal(expenseId);
        this.untrack(doc.expense.meetingId, expenseId);
        return 'uploaded';
      }
      doc = next;
      await this.dataService.putDoc(doc);
    }
    return skippedNotes ? 'rejected' : 'uploaded';
  }

  private async runUploadStep(doc: MeetingExpenseDoc, step: ExpenseUploadStep, serverNoteIds: Set<string>): Promise<void> {
    const expense = doc.expense;
    switch (step.kind) {
      case 'create':
        await this.dataService.remoteCreate(expense, this.authService.user?.defaultCurrencyID ?? null);
        return;
      case 'fields':
        await this.dataService.remoteUpdate(expense);
        return;
      case 'note': {
        const { note, clearAttachment } = step.entry;
        if (note.existingAnnotationId) {
          await this.dataService.updateNote(note.existingAnnotationId, note, clearAttachment);
          return;
        }
        const sentBefore = note.clientId && serverNoteIds.has(note.clientId.toLowerCase()) ? note.clientId : undefined;
        const annotationId = sentBefore ?? (await this.dataService.createNote(expense.id, note));
        // An open form still holds this note by client id; later edits must reach the server note.
        if (note.clientId) this.noteIds.set(note.clientId, Promise.resolve(annotationId));
        return;
      }
      case 'noteDelete':
        await this.dataService.removeNote(step.annotationId).catch((err) => {
          if (Number(err?.status) !== 404) throw err;
        });
        return;
      case 'submit':
        await this.dataService.remoteSubmit(expense.id);
        return;
      case 'scrap':
        await this.dataService.remoteScrap(expense.id);
        return;
    }
  }

  private async adoptServerCopy(doc: MeetingExpenseDoc, server: MeetingExpense | undefined): Promise<void> {
    const { id, meetingId } = doc.expense;
    await this.dataService.replaceWithServer(id, server);
    if (server && !isExpenseCancelled(server)) this.trackActive(meetingId, id, server.statusCode);
    else this.untrack(meetingId, id);
  }

  // A 4xx will never succeed on retry, so the server copy is read afresh: earlier steps may already have landed.
  private async discardRejected(doc: MeetingExpenseDoc): Promise<UploadOutcome> {
    const id = doc.expense.id;
    const server = (await this.dataService.fetchServerExpenses([id])).get(meetingKey(id));
    await this.adoptServerCopy(doc, server);
    return 'rejected';
  }

  private async revertSubmit(doc: MeetingExpenseDoc): Promise<UploadOutcome> {
    const reverted = completeStep(doc, { kind: 'submit' }) ?? doc;
    const expense = { ...reverted.expense, statusCode: EXPENSE_STATUS_CODE.DRAFT, statusLabel: undefined };
    await this.dataService.putDoc({ ...reverted, expense });
    this.trackActive(expense.meetingId, expense.id, EXPENSE_STATUS_CODE.DRAFT);
    return 'reverted';
  }

  private notifyQueued(key: string): void {
    this.notificationService.notify(this.translate.instant(key), 'Meeting Expenses', 'top', ToastStyle.INFO);
  }

  private notifyUpload(key: string, count: number, style: ToastStyle): void {
    if (count > 0) this.notificationService.notify(this.translate.instant(key, { count }), 'Meeting Expenses', 'top', style);
  }

  // Writes for one expense run in order so a later autosave can never land before an earlier one.
  private enqueue<T>(expenseId: string, operation: () => Promise<T>): Promise<T> {
    const result = (this.persistChains.get(expenseId) ?? Promise.resolve()).then(operation);
    const tail = result.then(() => undefined, () => undefined);
    this.persistChains.set(expenseId, tail);
    void tail.then(() => {
      if (this.persistChains.get(expenseId) === tail) this.persistChains.delete(expenseId);
    });
    return result;
  }
}
